import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link, Navigate } from 'react-router-dom';
import {
  collection,
  query,
  orderBy,
  limit,
  startAfter,
  getDocs,
  deleteDoc,
  doc,
  where,
  Timestamp,
} from 'firebase/firestore';
import { db } from '../../firebase';
import { useAuth } from '../Auth/Auth';
import BreadCrumb from '../BreadCrumb';
import { COLLECTIONS } from '../../constants/collections';
import { MoonLoader } from 'react-spinners';
import {
  ExclamationTriangleIcon,
  TrashIcon,
  FunnelIcon,
  ArrowPathIcon,
  ChevronUpIcon,
  ChevronDownIcon,
  InboxIcon,
} from '@heroicons/react/24/outline';

const pages = [
  { name: 'Administration', to: '/admin', component: Link, current: false },
  { name: 'Event Logs', to: '/admin/eventlogs', component: Link, current: true },
];

const BATCH_SIZE = 50;
const PAGE_SIZE = 15;

const EventLogViewer = () => {
  const { role, loading: authLoading } = useAuth();

  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [lastDoc, setLastDoc] = useState(null);
  const [hasMore, setHasMore] = useState(true);

  // Filters
  const [levelFilter, setLevelFilter] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [sourceFilter, setSourceFilter] = useState('');

  // Pagination (client-side over fetched logs)
  const [currentPage, setCurrentPage] = useState(1);

  // Sorting
  const [sortField, setSortField] = useState('createdAt');
  const [sortDir, setSortDir] = useState('desc');

  // Selection
  const [selected, setSelected] = useState(new Set());

  // Auto-refresh
  const [autoRefresh, setAutoRefresh] = useState(false);
  const intervalRef = useRef(null);

  const buildQuery = useCallback(
    (cursor) => {
      const constraints = [orderBy('createdAt', 'desc'), limit(BATCH_SIZE)];

      if (levelFilter) {
        constraints.unshift(where('level', '==', levelFilter));
      }
      if (dateFrom) {
        const from = new Date(dateFrom);
        from.setHours(0, 0, 0, 0);
        constraints.unshift(where('createdAt', '>=', Timestamp.fromDate(from)));
      }
      if (dateTo) {
        const to = new Date(dateTo);
        to.setHours(23, 59, 59, 999);
        constraints.unshift(where('createdAt', '<=', Timestamp.fromDate(to)));
      }
      if (cursor) {
        constraints.push(startAfter(cursor));
      }

      return query(collection(db, COLLECTIONS.EVENT_LOGS), ...constraints);
    },
    [levelFilter, dateFrom, dateTo]
  );

  const fetchLogs = useCallback(
    async (append = false) => {
      setLoading(true);
      try {
        const q = buildQuery(append ? lastDoc : null);
        const snapshot = await getDocs(q);
        const fetched = snapshot.docs.map((d) => ({ id: d.id, ...d.data(), _doc: d }));

        if (append) {
          setLogs((prev) => [...prev, ...fetched]);
        } else {
          setLogs(fetched);
          setCurrentPage(1);
          setSelected(new Set());
        }

        setLastDoc(snapshot.docs[snapshot.docs.length - 1] || null);
        setHasMore(snapshot.docs.length === BATCH_SIZE);
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error('Failed to fetch event logs', err);
      } finally {
        setLoading(false);
      }
    },
    [buildQuery, lastDoc]
  );

  // Initial fetch
  useEffect(() => {
    if (role === 'Admin') fetchLogs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [role]);

  // Auto-refresh
  useEffect(() => {
    if (autoRefresh) {
      intervalRef.current = setInterval(() => fetchLogs(), 30000);
    }
    return () => clearInterval(intervalRef.current);
  }, [autoRefresh, fetchLogs]);

  const handleApplyFilters = () => {
    setLastDoc(null);
    setHasMore(true);
    fetchLogs();
  };

  const handleClearFilters = () => {
    setLevelFilter('');
    setDateFrom('');
    setDateTo('');
    setSourceFilter('');
    setLastDoc(null);
    setHasMore(true);
    setTimeout(() => fetchLogs(), 0);
  };

  const handleDelete = async (id) => {
    try {
      await deleteDoc(doc(db, COLLECTIONS.EVENT_LOGS, id));
      setLogs((prev) => prev.filter((l) => l.id !== id));
      setSelected((prev) => {
        const next = new Set(prev);
        next.delete(id);
        return next;
      });
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('Failed to delete log', err);
    }
  };

  const handleBulkDelete = async () => {
    if (selected.size === 0) return;
    try {
      await Promise.all(
        Array.from(selected).map((id) => deleteDoc(doc(db, COLLECTIONS.EVENT_LOGS, id)))
      );
      setLogs((prev) => prev.filter((l) => !selected.has(l.id)));
      setSelected(new Set());
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('Failed to bulk delete logs', err);
    }
  };

  const toggleSelect = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    const pageItems = paginatedLogs();
    if (pageItems.every((l) => selected.has(l.id))) {
      setSelected((prev) => {
        const next = new Set(prev);
        pageItems.forEach((l) => next.delete(l.id));
        return next;
      });
    } else {
      setSelected((prev) => {
        const next = new Set(prev);
        pageItems.forEach((l) => next.add(l.id));
        return next;
      });
    }
  };

  // Client-side source filter + sorting
  const filteredLogs = () => {
    let result = logs;
    if (sourceFilter) {
      const s = sourceFilter.toLowerCase();
      result = result.filter(
        (l) => l.source?.toLowerCase().includes(s) || l.message?.toLowerCase().includes(s)
      );
    }
    if (sortField) {
      result = [...result].sort((a, b) => {
        let aVal = a[sortField];
        let bVal = b[sortField];
        if (sortField === 'createdAt') {
          aVal = aVal?.toMillis?.() || 0;
          bVal = bVal?.toMillis?.() || 0;
        }
        if (typeof aVal === 'string') aVal = aVal.toLowerCase();
        if (typeof bVal === 'string') bVal = bVal.toLowerCase();
        if (aVal < bVal) return sortDir === 'asc' ? -1 : 1;
        if (aVal > bVal) return sortDir === 'asc' ? 1 : -1;
        return 0;
      });
    }
    return result;
  };

  const paginatedLogs = () => {
    const all = filteredLogs();
    const start = (currentPage - 1) * PAGE_SIZE;
    return all.slice(start, start + PAGE_SIZE);
  };

  const totalPages = Math.ceil(filteredLogs().length / PAGE_SIZE) || 1;

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortField(field);
      setSortDir('asc');
    }
  };

  const SortIcon = ({ field }) => {
    if (sortField !== field) return null;
    return sortDir === 'asc' ? (
      <ChevronUpIcon className="inline w-4 h-4 ml-1" />
    ) : (
      <ChevronDownIcon className="inline w-4 h-4 ml-1" />
    );
  };

  // Stats
  const errorCount = logs.filter((l) => l.level === 'error').length;
  const warnCount = logs.filter((l) => l.level === 'warn').length;

  const formatTimestamp = (ts) => {
    if (!ts) return '—';
    const date = ts.toDate ? ts.toDate() : new Date(ts);
    return date.toLocaleString();
  };

  if (authLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gradient-to-br from-slate-50 to-gray-50">
        <div className="text-center">
          <MoonLoader size={60} color="#64748B" />
          <p className="mt-4 text-sm font-medium text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  if (role !== 'Admin') {
    return <Navigate to="/unauthorized" replace />;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-gray-50 to-blue-50 py-4">
      <BreadCrumb pages={pages} />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-6 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 bg-gradient-to-br from-red-500 to-rose-600 rounded-2xl flex items-center justify-center shadow-lg">
              <ExclamationTriangleIcon className="w-8 h-8 text-white" />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Event Logs</h1>
              <p className="text-sm text-gray-600 mt-1">Diagnostic warnings and errors</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={() => setAutoRefresh((v) => !v)}
              className={`px-4 py-2 rounded-xl text-sm font-medium transition-colors ${
                autoRefresh
                  ? 'bg-green-100 text-green-700 hover:bg-green-200'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              <ArrowPathIcon className={`inline w-4 h-4 mr-1 ${autoRefresh ? 'animate-spin' : ''}`} />
              {autoRefresh ? 'Auto ON' : 'Auto OFF'}
            </button>
            <button
              onClick={() => fetchLogs()}
              className="px-4 py-2 bg-white border border-gray-300 rounded-xl text-sm font-medium text-gray-700 hover:bg-gray-50"
            >
              <ArrowPathIcon className="inline w-4 h-4 mr-1" />
              Refresh
            </button>
          </div>
        </div>

        {/* Stats bar */}
        <div className="grid grid-cols-3 gap-4 mb-6">
          <div className="bg-white rounded-xl shadow p-4 text-center">
            <p className="text-2xl font-bold text-gray-900">{logs.length}</p>
            <p className="text-sm text-gray-500">Total Loaded</p>
          </div>
          <div className="bg-white rounded-xl shadow p-4 text-center">
            <p className="text-2xl font-bold text-red-600">{errorCount}</p>
            <p className="text-sm text-gray-500">Errors</p>
          </div>
          <div className="bg-white rounded-xl shadow p-4 text-center">
            <p className="text-2xl font-bold text-yellow-600">{warnCount}</p>
            <p className="text-sm text-gray-500">Warnings</p>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-xl shadow p-4 mb-6">
          <div className="flex flex-wrap items-end gap-4">
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Level</label>
              <select
                value={levelFilter}
                onChange={(e) => setLevelFilter(e.target.value)}
                className="block w-32 rounded-lg border-gray-300 shadow-sm text-sm focus:ring-indigo-500 focus:border-indigo-500"
              >
                <option value="">All</option>
                <option value="warn">Warn</option>
                <option value="error">Error</option>
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">From</label>
              <input
                type="date"
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
                className="block rounded-lg border-gray-300 shadow-sm text-sm focus:ring-indigo-500 focus:border-indigo-500"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">To</label>
              <input
                type="date"
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
                className="block rounded-lg border-gray-300 shadow-sm text-sm focus:ring-indigo-500 focus:border-indigo-500"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Source / Message</label>
              <input
                type="text"
                placeholder="Filter text..."
                value={sourceFilter}
                onChange={(e) => setSourceFilter(e.target.value)}
                className="block w-48 rounded-lg border-gray-300 shadow-sm text-sm focus:ring-indigo-500 focus:border-indigo-500"
              />
            </div>
            <button
              onClick={handleApplyFilters}
              className="px-4 py-2 bg-indigo-600 text-white rounded-lg text-sm font-medium hover:bg-indigo-700"
            >
              <FunnelIcon className="inline w-4 h-4 mr-1" />
              Apply
            </button>
            <button
              onClick={handleClearFilters}
              className="px-4 py-2 bg-gray-100 text-gray-600 rounded-lg text-sm font-medium hover:bg-gray-200"
            >
              Clear
            </button>
          </div>
        </div>

        {/* Bulk actions */}
        {selected.size > 0 && (
          <div className="bg-red-50 border border-red-200 rounded-xl p-3 mb-4 flex items-center justify-between">
            <span className="text-sm text-red-700 font-medium">{selected.size} selected</span>
            <button
              onClick={handleBulkDelete}
              className="px-4 py-1.5 bg-red-600 text-white rounded-lg text-sm font-medium hover:bg-red-700"
            >
              <TrashIcon className="inline w-4 h-4 mr-1" />
              Delete Selected
            </button>
          </div>
        )}

        {/* Table */}
        {loading && logs.length === 0 ? (
          <div className="flex justify-center py-20">
            <MoonLoader size={40} color="#64748B" />
          </div>
        ) : filteredLogs().length === 0 ? (
          <div className="bg-white rounded-xl shadow p-16 text-center">
            <InboxIcon className="mx-auto w-16 h-16 text-gray-300" />
            <p className="mt-4 text-lg font-medium text-gray-500">No event logs found</p>
            <p className="text-sm text-gray-400 mt-1">Warnings and errors will appear here when logged.</p>
          </div>
        ) : (
          <div className="bg-white rounded-xl shadow overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 w-10">
                      <input
                        type="checkbox"
                        checked={paginatedLogs().length > 0 && paginatedLogs().every((l) => selected.has(l.id))}
                        onChange={toggleSelectAll}
                        className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                      />
                    </th>
                    <th
                      className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase cursor-pointer select-none"
                      onClick={() => handleSort('level')}
                    >
                      Level <SortIcon field="level" />
                    </th>
                    <th
                      className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase cursor-pointer select-none"
                      onClick={() => handleSort('message')}
                    >
                      Message <SortIcon field="message" />
                    </th>
                    <th
                      className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase cursor-pointer select-none"
                      onClick={() => handleSort('source')}
                    >
                      Source <SortIcon field="source" />
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                      User
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                      URL
                    </th>
                    <th
                      className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase cursor-pointer select-none"
                      onClick={() => handleSort('createdAt')}
                    >
                      Timestamp <SortIcon field="createdAt" />
                    </th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {paginatedLogs().map((log) => (
                    <tr key={log.id} className="hover:bg-gray-50">
                      <td className="px-4 py-3">
                        <input
                          type="checkbox"
                          checked={selected.has(log.id)}
                          onChange={() => toggleSelect(log.id)}
                          className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                        />
                      </td>
                      <td className="px-4 py-3">
                        <span
                          className={`inline-flex px-2 py-0.5 rounded-full text-xs font-semibold ${
                            log.level === 'error'
                              ? 'bg-red-100 text-red-700'
                              : 'bg-yellow-100 text-yellow-700'
                          }`}
                        >
                          {log.level}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-700 max-w-xs truncate" title={log.message}>
                        {log.message}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-500 max-w-[120px] truncate" title={log.source}>
                        {log.source || '—'}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-500 max-w-[140px] truncate" title={log.userEmail}>
                        {log.userEmail || '—'}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-500 max-w-[160px] truncate" title={log.url}>
                        {log.url || '—'}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-500 whitespace-nowrap">
                        {formatTimestamp(log.createdAt)}
                      </td>
                      <td className="px-4 py-3 text-right">
                        <button
                          onClick={() => handleDelete(log.id)}
                          className="text-red-500 hover:text-red-700"
                          title="Delete"
                        >
                          <TrashIcon className="w-4 h-4" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            <div className="flex items-center justify-between px-4 py-3 border-t border-gray-200 bg-gray-50">
              <p className="text-sm text-gray-500">
                Page {currentPage} of {totalPages} &middot; {filteredLogs().length} logs loaded
              </p>
              <div className="flex items-center gap-1">
                {Array.from({ length: Math.min(totalPages, 10) }, (_, i) => i + 1).map((p) => (
                  <button
                    key={p}
                    onClick={() => setCurrentPage(p)}
                    className={`px-3 py-1 rounded text-sm font-medium ${
                      p === currentPage
                        ? 'bg-indigo-600 text-white'
                        : 'bg-white text-gray-600 hover:bg-gray-100 border border-gray-300'
                    }`}
                  >
                    {p}
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Load More */}
        {hasMore && !loading && logs.length > 0 && (
          <div className="mt-4 text-center">
            <button
              onClick={() => fetchLogs(true)}
              className="px-6 py-2 bg-white border border-gray-300 rounded-xl text-sm font-medium text-gray-700 hover:bg-gray-50 shadow-sm"
            >
              Load More
            </button>
          </div>
        )}
        {loading && logs.length > 0 && (
          <div className="flex justify-center py-4">
            <MoonLoader size={24} color="#64748B" />
          </div>
        )}
      </div>
    </div>
  );
};

export default EventLogViewer;
